import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { useChurchPlanner, usePlannerRoles } from '../../hooks/useChurchPlanner';
import { hasAnyPermission, Permission } from '../../utils/roles';
import {
  PlannerCard,
  PlannerError,
  PlannerHeading,
  PlannerInput,
  PlannerKicker,
  PlannerPage,
  PlannerSubtle,
  plannerColors,
} from './plannerTheme';
import { formatPlannerStatusLabel, normalizePlannerStatus } from './plannerStatus';

const PageShell = styled(PlannerPage)`
  gap: 1rem;
`;

const HeroCard = styled(PlannerCard)`
  padding: 1.2rem 1.25rem;
  background:
    radial-gradient(circle at top right, rgba(229, 211, 179, 0.16), transparent 30%),
    linear-gradient(180deg, #232326 0%, #19191b 100%);
`;

const HeroTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const HeroIdentity = styled.div`
  display: grid;
  gap: 0.35rem;
`;

const BackLink = styled(Link)`
  color: ${plannerColors.accent};
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 700;

  &:hover {
    text-decoration: underline;
  }
`;

const HeroMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.95rem;
`;

const TabStrip = styled.div`
  margin-top: 0.9rem;
`;

const TabRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TabButton = styled.button<{ $active: boolean }>`
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(229, 211, 179, 0.65)' : 'rgba(229, 211, 179, 0.26)')};
  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(180deg, rgba(229, 211, 179, 0.22), rgba(229, 211, 179, 0.08))'
      : 'linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(0, 0, 0, 0.12))'};
  color: ${({ $active }) => ($active ? plannerColors.text : plannerColors.muted)};
  border-radius: 10px;
  padding: 0.5rem 0.8rem;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    color: ${plannerColors.text};
    border-color: rgba(229, 211, 179, 0.5);
  }
`;

const StatPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid ${plannerColors.border};
  border-radius: 999px;
  padding: 0.42rem 0.72rem;
  background: rgba(255, 255, 255, 0.03);
  color: ${plannerColors.text};
  font-size: 0.82rem;
  font-weight: 700;
`;

const SectionCard = styled(PlannerCard)`
  padding: 1rem 1.1rem;
`;

const SectionTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: ${plannerColors.text};
  font-size: 1rem;
  font-weight: 700;
`;

const BackButton = styled(BackLink)`
  white-space: nowrap;
`;

const RoleHint = styled.div`
  color: ${plannerColors.muted};
  font-size: 0.78rem;
  line-height: 1.45;
`;

const SaveRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surface};
  color: ${plannerColors.text};
  border-radius: 10px;
  padding: 0.48rem 0.78rem;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  border-radius: 12px;
  padding: 0.56rem 0.7rem;
  font-size: 0.86rem;
`;

const UnsavedIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid rgba(229, 211, 179, 0.45);
  border-radius: 999px;
  padding: 0.22rem 0.54rem;
  background: rgba(229, 211, 179, 0.12);
  color: ${plannerColors.accent};
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const RoleSelectionShell = styled.div`
  margin-top: 0.75rem;
  display: grid;
  gap: 0.7rem;
  max-width: min(760px, 100%);
`;

const RoleGroupGrid = styled.div`
  display: grid;
  gap: 0.65rem;
`;

const RoleGroupCard = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 14px;
  background: ${plannerColors.surfaceHover};
  padding: 0.7rem 0.75rem;
  display: grid;
  gap: 0.6rem;
`;

const RoleGroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
`;

const RoleGroupTitle = styled.div`
  color: ${plannerColors.text};
  font-size: 0.83rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const RoleCount = styled.span`
  color: ${plannerColors.muted};
  font-size: 0.75rem;
`;

const SecondaryRoleList = styled.div`
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
`;

const RoleCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: ${plannerColors.text};
  font-size: 0.84rem;
  border: 1px solid ${plannerColors.border};
  border-radius: 10px;
  padding: 0.45rem 0.55rem;
  background: rgba(255, 255, 255, 0.02);
`;

const RoleCheckbox = styled.input`
  accent-color: ${plannerColors.accent};
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const SecondaryChip = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.26rem 0.58rem;
  border: 1px solid ${plannerColors.border};
  background: rgba(255, 255, 255, 0.03);
  color: ${plannerColors.text};
  font-size: 0.78rem;
  font-weight: 600;
`;

const InlineActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
`;

const AssignmentGrid = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const AssignmentCard = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 16px;
  padding: 0.9rem;
  background: ${plannerColors.surfaceHover};
  display: grid;
  gap: 0.35rem;
`;

const AssignmentTitle = styled.div`
  color: ${plannerColors.text};
  font-size: 0.95rem;
  font-weight: 700;
`;

const MetaGrid = styled.div`
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  margin-top: 0.85rem;
`;

const MetaCard = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 14px;
  padding: 0.72rem 0.8rem;
  background: rgba(255, 255, 255, 0.025);
`;

const MetaLabel = styled.div`
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${plannerColors.muted};
  font-weight: 700;
`;

const MetaValue = styled.div`
  margin-top: 0.3rem;
  color: ${plannerColors.text};
  font-size: 0.88rem;
  line-height: 1.35;
`;

type PersonDetailTab = 'Roles' | 'Schedule';

function formatDateTime(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDateOnly(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatUnavailableRange(startsAt: string, endsAt: string) {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${startsAt} to ${endsAt}`;
  }

  const displayEnd = end.getTime() > start.getTime() ? new Date(end.getTime() - 1) : end;
  return `${formatDateOnly(start.toISOString())} to ${formatDateOnly(displayEnd.toISOString())}`;
}

function getRoleName(entryRole: { name: string } | Array<{ name: string }> | null | undefined) {
  if (!entryRole) return null;
  if (Array.isArray(entryRole)) return entryRole[0]?.name ?? null;
  return entryRole.name ?? null;
}

function normalizedRoleKeys(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  return input
    .filter((role): role is string => typeof role === 'string')
    .map((role) => role.trim().toLowerCase())
    .filter(Boolean);
}

function areStringArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) return false;
  }
  return true;
}

export default function ChurchPlannerPersonDetailPage() {
  const { personId = '' } = useParams<{ personId: string }>();
  const { user } = useAuth();
  const planner = useChurchPlanner(true, user?.id);
  const rolesQuery = usePlannerRoles(true);
  const { data: currentUserProfile } = useCurrentUserProfile(user?.id);
  const person = planner.getPersonDetails(personId);
  const [selectedPrimaryRole, setSelectedPrimaryRole] = useState<string>('');
  const [selectedSecondaryRoleIds, setSelectedSecondaryRoleIds] = useState<string[]>([]);
  const [secondaryRoleSearch, setSecondaryRoleSearch] = useState('');
  const [activeTab, setActiveTab] = useState<PersonDetailTab>('Roles');

  const currentUserRoles = useMemo(
    () => normalizedRoleKeys(currentUserProfile?.roles ?? user?.user_metadata?.roles),
    [currentUserProfile?.roles, user?.user_metadata?.roles]
  );

  const canManageDefaultRole =
    hasAnyPermission(currentUserProfile?.roles ?? user?.user_metadata?.roles, [
      Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS,
      Permission.MANAGE_CHURCH_PLANNER_ROLES,
    ]) || currentUserRoles.includes('service_planner_team_leader');

  const primaryRoleId = person?.primaryPlannerRoleId || person?.defaultPlannerRoleId || '';
  const sourceSecondaryRoleIdsKey = (person?.secondaryPlannerRoleIds || []).join('|');

  const secondaryRoleIds = useMemo(
    () =>
      sourceSecondaryRoleIdsKey
        .split('|')
        .filter(Boolean)
        .filter((roleId) => roleId !== primaryRoleId),
    [primaryRoleId, sourceSecondaryRoleIdsKey]
  );

  useEffect(() => {
    setSelectedPrimaryRole((prev) => (prev === primaryRoleId ? prev : primaryRoleId));
    setSelectedSecondaryRoleIds((prev) => (areStringArraysEqual(prev, secondaryRoleIds) ? prev : secondaryRoleIds));
  }, [person?.id, primaryRoleId, secondaryRoleIds]);

  const rolesByArea = useMemo(() => {
    const groups = new Map<string, { key: string; name: string; roles: Array<{ id: string; name: string; area?: string | null }> }>();
    (rolesQuery.data ?? []).forEach((role) => {
      const key = (role.area || '').trim().toLowerCase() || 'general';
      const name = (role.area || '').trim() || 'General';
      const existing = groups.get(key);
      if (existing) {
        existing.roles.push(role);
      } else {
        groups.set(key, { key, name, roles: [role] });
      }
    });
    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        roles: [...group.roles].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rolesQuery.data]);

  const normalizedSecondarySearch = secondaryRoleSearch.trim().toLowerCase();
  const filteredRoleGroups = useMemo(() => {
    if (!normalizedSecondarySearch) return rolesByArea;
    return rolesByArea
      .map((group) => ({
        ...group,
        roles: group.roles.filter((role) => role.name.toLowerCase().includes(normalizedSecondarySearch)),
      }))
      .filter((group) => group.roles.length > 0);
  }, [normalizedSecondarySearch, rolesByArea]);

  const calendarUnavailableEntries = useMemo(
    () =>
      (planner.memberUnavailability ?? [])
        .filter((entry) => entry.user_id === personId)
        .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()),
    [planner.memberUnavailability, personId]
  );

  const serviceUnavailableResponses = useMemo(() => {
    if (!person) return [];

    const planById = new Map((planner.plans ?? []).map((plan) => [plan.id, plan]));
    return (person.availability ?? [])
      .filter((entry) => {
        const normalized = (entry.availability_status || '').trim().toLowerCase();
        return normalized === 'unavailable' || normalized === 'declined';
      })
      .map((entry) => {
        const linkedPlan = planById.get(entry.service_plan_id);
        return {
          id: entry.id,
          title: linkedPlan?.title || 'Service plan',
          serviceDate: linkedPlan?.service_date || '',
          notes: (entry.notes || '').trim(),
        };
      })
      .sort((a, b) => {
        const left = a.serviceDate ? new Date(a.serviceDate).getTime() : Number.MAX_SAFE_INTEGER;
        const right = b.serviceDate ? new Date(b.serviceDate).getTime() : Number.MAX_SAFE_INTEGER;
        return left - right;
      });
  }, [person, planner.plans]);

  if (planner.isLoading || rolesQuery.isLoading) {
    return (
      <PageShell>
        <PlannerCard>
          <PlannerSubtle>Loading profile...</PlannerSubtle>
        </PlannerCard>
      </PageShell>
    );
  }

  if (planner.isError || rolesQuery.isError) {
    return (
      <PageShell>
        <PlannerCard>
          <PlannerError>Unable to load planner person details right now.</PlannerError>
        </PlannerCard>
      </PageShell>
    );
  }

  if (!person) {
    return (
      <PageShell>
        <PlannerCard>
          <SectionTitleRow>
            <div>
              <PlannerKicker>Planner Member</PlannerKicker>
              <PlannerHeading>Person not found</PlannerHeading>
            </div>
            <BackButton to="/church-planner/people">Back to People</BackButton>
          </SectionTitleRow>
          <PlannerSubtle>This planner member could not be found.</PlannerSubtle>
        </PlannerCard>
      </PageShell>
    );
  }

  const savePrimaryRole = async () => {
    try {
      await planner.updateMemberDefaultRole({
        userId: person.id,
        roleId: selectedPrimaryRole || null,
      });
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to save primary role.');
    }
  };

  const saveSecondaryRoles = async () => {
    try {
      await planner.updateMemberSecondaryRoles({
        userId: person.id,
        roleIds: selectedSecondaryRoleIds.filter((roleId) => roleId !== selectedPrimaryRole),
      });
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to save secondary roles.');
    }
  };

  const primaryRoleName = selectedPrimaryRole
    ? (rolesQuery.data ?? []).find((role) => role.id === selectedPrimaryRole)?.name || 'Assigned'
    : 'Not set';
  const secondaryRoleNames = selectedSecondaryRoleIds
    .map((roleId) => (rolesQuery.data ?? []).find((role) => role.id === roleId)?.name)
    .filter((name): name is string => !!name);
  const hasUnsavedPrimaryRole = selectedPrimaryRole !== primaryRoleId;

  return (
    <PageShell>
      <HeroCard>
        <HeroTop>
          <HeroIdentity>
            <PlannerKicker>Planner Member</PlannerKicker>
            <PlannerHeading>{person.name}</PlannerHeading>
            <PlannerSubtle>{person.email || 'No email available'}</PlannerSubtle>
            <PlannerSubtle>{person.title || 'Not set'}</PlannerSubtle>
          </HeroIdentity>
          <BackButton to="/church-planner/people">Back to People</BackButton>
        </HeroTop>
        <HeroMetaRow>
          <StatPill>Assignments: {person.upcomingAssignments.length + person.historyAssignments.length}</StatPill>
          <StatPill>Primary: {primaryRoleName}</StatPill>
          <StatPill>Secondary: {secondaryRoleNames.length ? secondaryRoleNames.join(', ') : 'None'}</StatPill>
        </HeroMetaRow>
        <TabStrip>
          <TabRow>
            {(['Roles', 'Schedule'] as PersonDetailTab[]).map((tab) => (
              <TabButton key={tab} $active={activeTab === tab} onClick={() => setActiveTab(tab)}>
                {tab}
              </TabButton>
            ))}
          </TabRow>
        </TabStrip>
      </HeroCard>

      {activeTab === 'Roles' ? (
        <>
          <SectionCard>
            <SectionTitleRow>
              <SectionTitle>Profile summary</SectionTitle>
              <PlannerSubtle>
                {canManageDefaultRole ? 'Admins and team leaders can edit.' : 'Read only'}
              </PlannerSubtle>
            </SectionTitleRow>
            <MetaGrid>
              <MetaCard>
                <MetaLabel>Planner access</MetaLabel>
                <MetaValue>
                  {person.plannerAccessRoles.length ? person.plannerAccessRoles.join(', ') : 'None'}
                </MetaValue>
              </MetaCard>
              <MetaCard>
                <MetaLabel>Primary role</MetaLabel>
                <MetaValue>{primaryRoleName}</MetaValue>
              </MetaCard>
              <MetaCard>
                <MetaLabel>Secondary roles</MetaLabel>
                <MetaValue>{secondaryRoleNames.length ? secondaryRoleNames.join(', ') : 'None'}</MetaValue>
              </MetaCard>
              <MetaCard>
                <MetaLabel>Upcoming availability</MetaLabel>
                <MetaValue>{planner.upcomingPlans.slice(0, 10).length} services visible</MetaValue>
              </MetaCard>
            </MetaGrid>
          </SectionCard>

          <SectionCard>
            <SectionTitleRow>
              <SectionTitle>Primary Planner Role (Default)</SectionTitle>
              <PlannerSubtle>{canManageDefaultRole ? 'Admins and team leaders can edit.' : 'Read only'}</PlannerSubtle>
            </SectionTitleRow>
            {canManageDefaultRole ? (
              <SaveRow style={{ marginTop: '0.75rem' }}>
                <Select
                  value={selectedPrimaryRole}
                  disabled={!canManageDefaultRole}
                  onChange={(event) => {
                    const nextPrimaryRoleId = event.target.value;
                    setSelectedPrimaryRole(nextPrimaryRoleId);
                    setSelectedSecondaryRoleIds((prev) => prev.filter((roleId) => roleId !== nextPrimaryRoleId));
                  }}
                >
                  <option value="">No default role</option>
                  {rolesByArea.map((group) => (
                    <optgroup key={group.key} label={group.name}>
                      {group.roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
                {hasUnsavedPrimaryRole ? <UnsavedIndicator>Unsaved</UnsavedIndicator> : null}
                <ActionButton type="button" disabled={!canManageDefaultRole || planner.isMutating} onClick={savePrimaryRole}>
                  Save primary role
                </ActionButton>
              </SaveRow>
            ) : (
              <RoleHint style={{ marginTop: '0.75rem' }}>
                Current primary role: {primaryRoleName}
              </RoleHint>
            )}
          </SectionCard>

          <SectionCard>
            <SectionTitleRow>
              <SectionTitle>Secondary Roles</SectionTitle>
              <PlannerSubtle>
                {canManageDefaultRole ? `${selectedSecondaryRoleIds.length} selected` : 'Read only'}
              </PlannerSubtle>
            </SectionTitleRow>
            <RoleHint style={{ marginTop: '0.4rem' }}>
              Secondary roles are used when no eligible primary role match is available.
            </RoleHint>
            {canManageDefaultRole ? (
              <RoleSelectionShell>
                <PlannerInput
                  type="search"
                  value={secondaryRoleSearch}
                  disabled={!canManageDefaultRole}
                  placeholder="Filter secondary roles by name"
                  onChange={(event) => setSecondaryRoleSearch(event.target.value)}
                />
                {secondaryRoleNames.length ? (
                  <ChipRow>
                    {secondaryRoleNames.map((name) => (
                      <SecondaryChip key={name}>{name}</SecondaryChip>
                    ))}
                  </ChipRow>
                ) : null}
                <InlineActions>
                  <ActionButton
                    type="button"
                    disabled={!canManageDefaultRole || selectedSecondaryRoleIds.length === 0}
                    onClick={() => setSelectedSecondaryRoleIds([])}
                  >
                    Clear all
                  </ActionButton>
                </InlineActions>
                <RoleGroupGrid>
                  {filteredRoleGroups.map((group) => {
                    const selectedCount = group.roles.filter((role) => selectedSecondaryRoleIds.includes(role.id)).length;
                    return (
                      <RoleGroupCard key={group.key}>
                        <RoleGroupHeader>
                          <RoleGroupTitle>{group.name}</RoleGroupTitle>
                          <RoleCount>
                            {selectedCount}/{group.roles.length} selected
                          </RoleCount>
                        </RoleGroupHeader>
                        <SecondaryRoleList>
                          {group.roles.map((role) => {
                            const isDisabled = role.id === selectedPrimaryRole;
                            const isChecked = selectedSecondaryRoleIds.includes(role.id);
                            return (
                              <RoleCheckboxLabel key={role.id}>
                                <RoleCheckbox
                                  type="checkbox"
                                  checked={isChecked}
                                  disabled={!canManageDefaultRole || isDisabled}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    setSelectedSecondaryRoleIds((prev) => {
                                      if (checked) {
                                        return prev.includes(role.id) ? prev : [...prev, role.id];
                                      }
                                      return prev.filter((roleId) => roleId !== role.id);
                                    });
                                  }}
                                />
                                <span>{role.name}</span>
                              </RoleCheckboxLabel>
                            );
                          })}
                        </SecondaryRoleList>
                      </RoleGroupCard>
                    );
                  })}
                </RoleGroupGrid>
                <ActionButton type="button" disabled={!canManageDefaultRole || planner.isMutating} onClick={saveSecondaryRoles}>
                  Save secondary roles
                </ActionButton>
              </RoleSelectionShell>
            ) : (
              <RoleHint style={{ marginTop: '0.75rem' }}>
                Current secondary roles: {secondaryRoleNames.length ? secondaryRoleNames.join(', ') : 'None'}
              </RoleHint>
            )}
          </SectionCard>
        </>
      ) : (
        <>
          <SectionCard>
            <SectionTitleRow>
              <SectionTitle>Upcoming Assignments</SectionTitle>
            </SectionTitleRow>
            {person.upcomingAssignments.length === 0 ? (
              <PlannerSubtle style={{ marginTop: '0.65rem' }}>No upcoming assignments found.</PlannerSubtle>
            ) : (
              <AssignmentGrid style={{ marginTop: '0.75rem' }}>
                {person.upcomingAssignments.map((entry) => (
                  <AssignmentCard key={entry.assignment.id}>
                    <AssignmentTitle>{entry.plan.title}</AssignmentTitle>
                    <PlannerSubtle>{formatDateTime(entry.plan.service_date)}</PlannerSubtle>
                    <PlannerSubtle>Role: {getRoleName(entry.assignment.service_plan_roles) || 'Unassigned role'}</PlannerSubtle>
                    <PlannerSubtle>Status: {formatPlannerStatusLabel(normalizePlannerStatus(entry.assignment.status))}</PlannerSubtle>
                  </AssignmentCard>
                ))}
              </AssignmentGrid>
            )}
          </SectionCard>

          <SectionCard>
            <SectionTitleRow>
              <SectionTitle>Unavailable Dates (Calendar Blocks)</SectionTitle>
            </SectionTitleRow>
            <PlannerSubtle style={{ marginTop: '0.35rem' }}>
              Calendar-based unavailable date windows for this person.
            </PlannerSubtle>
            {calendarUnavailableEntries.length === 0 ? (
              <PlannerSubtle style={{ marginTop: '0.65rem' }}>No calendar unavailable dates found.</PlannerSubtle>
            ) : (
              <AssignmentGrid style={{ marginTop: '0.75rem' }}>
                {calendarUnavailableEntries.map((entry) => (
                  <AssignmentCard key={entry.id}>
                    <AssignmentTitle>{formatUnavailableRange(entry.starts_at, entry.ends_at)}</AssignmentTitle>
                    <PlannerSubtle>{(entry.notes || entry.reason || entry.title || 'Unavailable').trim()}</PlannerSubtle>
                  </AssignmentCard>
                ))}
              </AssignmentGrid>
            )}
          </SectionCard>

          <SectionCard>
            <SectionTitleRow>
              <SectionTitle>Marked Not Available (Service Responses)</SectionTitle>
            </SectionTitleRow>
            <PlannerSubtle style={{ marginTop: '0.35rem' }}>
              Service-level unavailable responses for specific plans.
            </PlannerSubtle>
            {serviceUnavailableResponses.length === 0 ? (
              <PlannerSubtle style={{ marginTop: '0.65rem' }}>No service-level unavailable responses found.</PlannerSubtle>
            ) : (
              <AssignmentGrid style={{ marginTop: '0.75rem' }}>
                {serviceUnavailableResponses.map((entry) => (
                  <AssignmentCard key={entry.id}>
                    <AssignmentTitle>{entry.title}</AssignmentTitle>
                    <PlannerSubtle>{entry.serviceDate ? formatDateTime(entry.serviceDate) : 'No service date'}</PlannerSubtle>
                    <PlannerSubtle>{entry.notes || 'Marked unavailable for this service.'}</PlannerSubtle>
                  </AssignmentCard>
                ))}
              </AssignmentGrid>
            )}
          </SectionCard>

          <SectionCard>
            <SectionTitleRow>
              <SectionTitle>Service History</SectionTitle>
            </SectionTitleRow>
            {person.historyAssignments.length === 0 ? (
              <PlannerSubtle style={{ marginTop: '0.65rem' }}>No service history found.</PlannerSubtle>
            ) : (
              <AssignmentGrid style={{ marginTop: '0.75rem' }}>
                {person.historyAssignments.slice(0, 15).map((entry) => (
                  <AssignmentCard key={entry.assignment.id}>
                    <AssignmentTitle>{entry.plan.title}</AssignmentTitle>
                    <PlannerSubtle>{formatDateTime(entry.plan.service_date)}</PlannerSubtle>
                    <PlannerSubtle>Role: {getRoleName(entry.assignment.service_plan_roles) || 'Unassigned role'}</PlannerSubtle>
                    <PlannerSubtle>Status: {formatPlannerStatusLabel(normalizePlannerStatus(entry.assignment.status))}</PlannerSubtle>
                  </AssignmentCard>
                ))}
              </AssignmentGrid>
            )}
          </SectionCard>
        </>
      )}
    </PageShell>
  );
}
